import type { ReactNode } from 'react';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';
import { generatePath, useLocation } from 'react-router-dom-v5-compat';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useModal } from '@/modules/modals';
import { Button, Heading, Icon, Link, Text, type IconProps } from '@/modules/ui';

import { IGNORE_SECURITY_AUDIT_REPORT_MODAL } from '../../constants';
import { selectIgnoreReportSecretApi } from '../../services/selectors';
import type { Secret } from '../../types';
import * as felaRules from './SecurityAuditSecretItem.rules';

export interface SecurityAuditSecretItemProps {
    id: Secret['id'];
    name: Secret['name'];
    additional?: ReactNode;
    icon?: IconProps['type'];
    isLastInGroup?: boolean;
    updateSecretRoute: string;
    canEdit: boolean;
    ignoreable?: boolean;
}

export const SecurityAuditSecretItem = ({
    id,
    name,
    additional,
    icon,
    updateSecretRoute,
    canEdit,
    isLastInGroup = false,
    ignoreable = false,
}: SecurityAuditSecretItemProps) => {
    const { css } = useFela({ isLastInGroup, icon });
    const { pathname, search } = useLocation();

    const { updateModal } = useModal<{
        id: Secret['id'];
    }>(IGNORE_SECURITY_AUDIT_REPORT_MODAL);

    const ignoreApi = useAppSelector(state => selectIgnoreReportSecretApi(state, id));

    return (
        <div className={css(felaRules.container)} data-testid={`security-audit-secret-${id}`}>
            <div className={css(felaRules.contentWrapper)}>
                <Heading color='black' elementType='h3' customStyle={icon ? felaRules.headingWithIcon : felaRules.heading}>
                    {icon ? <Icon type={icon} customStyle={felaRules.icon} /> : null}
                    {name}
                </Heading>
                {additional ? (
                    <Text variant='ParagraphSmallMedium' color='brownGrey2' customStyle={felaRules.additional}>
                        {additional}
                    </Text>
                ) : null}
            </div>
            <div className={css(felaRules.actionsWrapper)}>
                {canEdit ? (
                    <Link
                        color='blue'
                        type='router'
                        to={generatePath(updateSecretRoute, {
                            id,
                        })}
                        state={{ from: pathname + search }}
                        customStyle={felaRules.detailLink}
                    >
                        <FormattedMessage id='securityAudit.list.update' />
                    </Link>
                ) : null}
                {ignoreable && canEdit ? (
                    <Button
                        color='red'
                        type='link'
                        onClick={() => {
                            updateModal(true, {
                                id,
                            });
                        }}
                        disabled={ignoreApi.inProgress}
                        customStyle={felaRules.dismissButton}
                        data-testid='dismiss-button'
                    >
                        <FormattedMessage id='securityAudit.list.ignoreReport' />
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
